// src/store/modules/job-seeker-profile.module.js

import { set as idbSet, get as idbGet, del as idbDel } from "idb-keyval";

const PROFILE_STORAGE_KEY = "jobSeekerProfile";

function saveToLocalStorage(payload) {
  try {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    console.error("Failed to write profile to localStorage:", err);
  }
}

function loadFromLocalStorageRaw() {
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error("Failed to parse profile from localStorage:", err);
    return null;
  }
}

const state = () => ({
  basicInfo:      {},   // { email, phoneNumber, gender, dob, language, nationality, visaStatus, country, careerLevel, salaryExpectation, fullName? }
  qualification:  {},   // { degree, specialization, university, qualification_country, graduation_start, graduation_end }
  experience:     [],   // array of { jobStatus, jobTitle, group_id, industry_id, company, country, description, startDate, endDate, currentlyWorking, noticePeriod }
  skills:         [],   // array of strings
  resumeMeta:     null, // { name: "MyResume.pdf", url: "https://…/resumes/xxx.pdf" } or null
  audioMeta:      null, // { filename: "audio_XXXXX.webm", url: "https://…/audio_intros/xxx.webm" } or null
  videoMeta:      null, // { filename: "video_XXXXX.webm", url: "https://…/video_intros/xxx.webm" } or null
  resumeBlob:     null, // Blob in memory (used while editing before final submit)
  audioBlob:      null, // likewise for audio
  videoBlob:      null, // likewise for video
});

const mutations = {
  SET_BASIC_INFO(state, payload) {
    state.basicInfo = { ...payload };
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  SET_QUALIFICATION(state, payload) {
    state.qualification = { ...payload };
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  ADD_EXPERIENCE(state, payload) {
    state.experience.push({ ...payload });
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  SET_EXPERIENCE(state, payloadArray) {
    state.experience = Array.isArray(payloadArray) ? [...payloadArray] : [];
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  SET_SKILLS(state, payload) {
    state.skills = Array.isArray(payload) ? [...payload] : [];
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },

  // Resume metadata & Blob
  SET_RESUME_META(state, meta) {
    // meta = { name: "MyResume.pdf", url: "https://…/resumes/xxx.pdf" }
    state.resumeMeta = { ...meta };
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  SET_RESUME_BLOB(state, blob) {
    state.resumeBlob = blob; // actual Blob in memory
  },

  // Audio metadata & Blob
  SET_AUDIO_META(state, meta) {
    // meta = { filename: "audio_XXXXX.webm", url: "https://…/audio_intros/xxx.webm" }
    state.audioMeta = { ...meta };
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  SET_AUDIO_BLOB(state, blob) {
    state.audioBlob = blob;
  },

  // Video metadata & Blob
  SET_VIDEO_META(state, meta) {
    // meta = { filename: "video_XXXXX.webm", url: "https://…/video_intros/xxx.webm" }
    state.videoMeta = { ...meta };
    saveToLocalStorage({
      basicInfo:     state.basicInfo,
      qualification: state.qualification,
      experience:    state.experience,
      skills:        state.skills,
      resumeMeta:    state.resumeMeta,
      audioMeta:     state.audioMeta,
      videoMeta:     state.videoMeta,
    });
  },
  SET_VIDEO_BLOB(state, blob) {
    state.videoBlob = blob;
  },
};

const actions = {
  // Individual “save” actions (unchanged)
  saveBasicInfo({ commit }, data) {
    commit("SET_BASIC_INFO", data);
  },
  saveQualification({ commit }, data) {
    commit("SET_QUALIFICATION", data);
  },
  saveExperience({ commit }, data) {
    commit("ADD_EXPERIENCE", data);
  },
  saveSkills({ commit }, list) {
    commit("SET_SKILLS", list);
  },

  // Resume: store metadata & Blob in IndexedDB
  async saveResumeFile({ commit }, { blob, filename }) {
    commit("SET_RESUME_BLOB", blob);
    commit("SET_RESUME_META", { name: filename, url: null });
    try {
      await idbSet("profile-resume", blob);
    } catch (err) {
      console.error("Failed to store resume Blob in IndexedDB:", err);
    }
  },

  // Audio
  async saveAudioIntro({ commit }, { blob, filename }) {
    commit("SET_AUDIO_BLOB", blob);
    commit("SET_AUDIO_META", { filename, url: null });
    try {
      await idbSet("profile-audio", blob);
    } catch (err) {
      console.error("Failed to store audio Blob in IndexedDB:", err);
    }
  },

  // Video
  async saveVideoIntro({ commit }, { blob, filename }) {
    commit("SET_VIDEO_BLOB", blob);
    commit("SET_VIDEO_META", { filename, url: null });
    try {
      await idbSet("profile-video", blob);
    } catch (err) {
      console.error("Failed to store video Blob in IndexedDB:", err);
    }
  },

  // Load everything from localStorage & IndexedDB
  async loadFromLocalStorage({ commit }) {
    const saved = loadFromLocalStorageRaw();
    if (saved) {
      if (saved.basicInfo) {
        commit("SET_BASIC_INFO", saved.basicInfo);
      }
      if (saved.qualification) {
        commit("SET_QUALIFICATION", saved.qualification);
      }
      if (Array.isArray(saved.experience)) {
        commit("SET_EXPERIENCE", saved.experience);
      }
      if (Array.isArray(saved.skills)) {
        commit("SET_SKILLS", saved.skills);
      }
      if (saved.resumeMeta) {
        commit("SET_RESUME_META", saved.resumeMeta);
      }
      if (saved.audioMeta) {
        commit("SET_AUDIO_META", saved.audioMeta);
      }
      if (saved.videoMeta) {
        commit("SET_VIDEO_META", saved.videoMeta);
      }
    }

    // Rehydrate resumeBlob if meta exists
    if (saved && saved.resumeMeta && saved.resumeMeta.name) {
      try {
        const blob = await idbGet("profile-resume");
        if (blob instanceof Blob) {
          commit("SET_RESUME_BLOB", blob);
        }
      } catch (err) {
        console.error("Failed to load resume Blob from IndexedDB:", err);
      }
    }

    // Rehydrate audioBlob
    if (saved && saved.audioMeta && saved.audioMeta.filename) {
      try {
        const blob = await idbGet("profile-audio");
        if (blob instanceof Blob) {
          commit("SET_AUDIO_BLOB", blob);
        }
      } catch (err) {
        console.error("Failed to load audio Blob from IndexedDB:", err);
      }
    }

    // Rehydrate videoBlob
    if (saved && saved.videoMeta && saved.videoMeta.filename) {
      try {
        const blob = await idbGet("profile-video");
        if (blob instanceof Blob) {
          commit("SET_VIDEO_BLOB", blob);
        }
      } catch (err) {
        console.error("Failed to load video Blob from IndexedDB:", err);
      }
    }
  },

  /**
   * NEW ACTION: receives the entire backend payload (from CandidateProfileResource)
   * and commits each piece into state. Then it stores all “metadata” (excluding Blobs)
   * to localStorage so if the user refreshes they still see the same data.
   *
   * payload is assumed to have the structure:
   * {
   *   id,
   *   user_id,
   *   email,
   *   phone_number,
   *   gender,
   *   dob,
   *   language,
   *   nationality,
   *   visa_status,
   *   country,
   *   career_level,
   *   salary_expectation,
   *
   *   degree,
   *   specialization,
   *   university,
   *   qualification_country,
   *   graduation_start,
   *   graduation_end,
   *
   *   experience: [ { jobStatus, jobTitle, group_id, industry_id, company, country, description, startDate, endDate, currentlyWorking, noticePeriod }, … ],
   *   skills: [ "JavaScript", "Vue.js", … ],
   *
   *   resume_url,      // string or null
   *   audio_intro_url, // string or null
   *   video_intro_url, // string or null
   *
   *   bio,
   *   created_at,
   *   updated_at
   * }
   */
  setCandidateProfile({ commit }, payload) {
    // 1) Basic Info
    commit("SET_BASIC_INFO", {
      email:             payload.email || "",
      phoneNumber:       payload.phone_number || "",
      gender:            payload.gender || "",
      dob:               payload.dob || "",
      language:          payload.language || "",
      nationality:       payload.nationality || "",
      visaStatus:        payload.visa_status || "",
      country:           payload.country || "",
      careerLevel:       payload.career_level || "",
      salaryExpectation: payload.salary_expectation || "",
      // If you have first/last name separately, you could do:
      // fullName: payload.first_name + " " + payload.last_name
      fullName:          (payload.first_name && payload.last_name)
                           ? `${payload.first_name} ${payload.last_name}`
                           : "",
    });

    // 2) Qualification
    commit("SET_QUALIFICATION", {
      degree:                   payload.degree || "",
      specialization:           payload.specialization || "",
      university:               payload.university || "",
      qualification_country:    payload.qualification_country || "",
      graduation_start:         payload.graduation_start || "",
      graduation_end:           payload.graduation_end || "",
    });

    // 3) Experience (array)
    commit("SET_EXPERIENCE", Array.isArray(payload.experience) ? payload.experience : []);

    // 4) Skills (array)
    commit("SET_SKILLS", Array.isArray(payload.skills) ? payload.skills : []);

    // 5) Resume metadata: extract filename from URL, if present
    if (payload.resume_url) {
      // e.g. "https://example.com/storage/resumes/abcd1234.pdf"
      const segments = payload.resume_url.split("/");
      const filename = segments[segments.length - 1];
      commit("SET_RESUME_META", { name: filename, url: payload.resume_url });
      // We do NOT set a resumeBlob here because the user downloaded it from the server;
      // we only store URL. If they “Edit” later, they’ll re-upload or keep the same meta.
      commit("SET_RESUME_BLOB", null);
    } else {
      commit("SET_RESUME_META", null);
      commit("SET_RESUME_BLOB", null);
    }

    // 6) Audio Intro metadata
    if (payload.audio_intro_url) {
      const seg2 = payload.audio_intro_url.split("/");
      const filename2 = seg2[seg2.length - 1];
      commit("SET_AUDIO_META", { filename: filename2, url: payload.audio_intro_url });
      commit("SET_AUDIO_BLOB", null);
    } else {
      commit("SET_AUDIO_META", null);
      commit("SET_AUDIO_BLOB", null);
    }

    // 7) Video Intro metadata
    if (payload.video_intro_url) {
      const seg3 = payload.video_intro_url.split("/");
      const filename3 = seg3[seg3.length - 1];
      commit("SET_VIDEO_META", { filename: filename3, url: payload.video_intro_url });
      commit("SET_VIDEO_BLOB", null);
    } else {
      commit("SET_VIDEO_META", null);
      commit("SET_VIDEO_BLOB", null);
    }

    // Note: We do not explicitly store `bio`, `created_at`, etc. in this module,
    // but if you want to expose them you can add additional fields in state + mutations.
  },
};

const getters = {
  basicInfo:     (s) => s.basicInfo,
  qualification: (s) => s.qualification,
  experience:    (s) => s.experience,
  skills:        (s) => s.skills,
  resumeMeta:    (s) => s.resumeMeta,
  audioMeta:     (s) => s.audioMeta,
  videoMeta:     (s) => s.videoMeta,
  resumeBlob:    (s) => s.resumeBlob,
  audioBlob:     (s) => s.audioBlob,
  videoBlob:     (s) => s.videoBlob,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
